import { useState, useEffect, useRef, type ChangeEvent } from "react";
import { api, download, apiText } from "../api";
import { useLocation, navigate } from "../router";
import { useAsync } from "../hooks";
import { RefreshButton } from "./RefreshButton";
import { fmt, short, fmtMillis } from "../format";
import { DataTable } from "./DataTable";
import { CopyButton } from "./CopyButton";
import { Recipes } from "./Recipes";
import { useConfirmAction } from "./useConfirmAction";
import { SCHEDULES } from "./NewScraper";
import { Ribbon, RibbonLegend, toSegments, scarCount, hasCanary, type Segment } from "./Ribbon";
import { words, versionLabel, statusText } from "../vocabulary";
import type {
  Scraper,
  Run,
  ExtractorVersion,
  LatestData,
  ChangesData,
  TenantSummary,
} from "../types";

type FleetHistory = Record<string, { runs: Run[]; versions: ExtractorVersion[] }>;

/** Remembered fleet view mode (tiles vs table) — see the `view` state in {@link Scrapers}. */
const VIEW_KEY = "amendie.scrapersView";
function readView(): string | null {
  try {
    return localStorage.getItem(VIEW_KEY);
  } catch {
    return null;
  }
}

/**
 * Run history + version lineage for every scraper in the fleet, in ONE request.
 *
 * Was N+1 (2 fetches per scraper). `GET /fleet/history` returns runs + versions for every
 * scraper the caller can see (operator: all tenants; customer: their own), so the ribbon does a
 * single round-trip. `ids` is only a refetch trigger — the endpoint decides the fleet, not us.
 */
function useFleetHistory(scrapers: Scraper[]): Record<string, Segment[]> {
  const ids = scrapers.map((s) => s.id).join(",");
  const [map, setMap] = useState<Record<string, Segment[]>>({});

  useEffect(() => {
    if (!ids) return;
    let cancelled = false;
    void api<FleetHistory>("/fleet/history")
      .then((hist) => {
        if (cancelled) return;
        const next: Record<string, Segment[]> = {};
        for (const [id, h] of Object.entries(hist)) next[id] = toSegments(h.runs, h.versions);
        setMap(next);
      })
      .catch(() => {
        /* leave the ribbons empty rather than fail the whole Fleet on a history hiccup */
      });
    return () => {
      cancelled = true;
    };
  }, [ids]);

  return map;
}

export function Scrapers({
  onNew,
  operator,
  tenantFilter,
  onTenantFilter,
}: {
  onNew?: () => void;
  operator: boolean;
  /** Operator only: show just this client's scrapers (set by drilling in from Clients). */
  tenantFilter?: string | null;
  onTenantFilter?: (tenantId: string | null) => void;
}) {
  const { data, loading, error, reload } = useAsync<Scraper[]>(() => api("/scrapers"), []);
  // For an operator this list spans every tenant, so each row needs to say whose it is.
  // Customers get a 403 here and never need it — hence the operator gate.
  // GET /tenants is PAGINATED — `{ clients: TenantSummary[], … }`, NOT a bare array. Pull a big page for
  // the id→name map (operator fleet name resolution). Reading it as an array crashed the operator
  // Scrapers view (`.find` on a non-array) — the "open a client's Scrapers → error" bug.
  const { data: tenantPage } = useAsync<{ clients: TenantSummary[] }>(
    () => (operator ? api("/tenants?pageSize=200") : Promise.resolve({ clients: [] })),
    [operator],
  );
  const { pathname } = useLocation();
  // View mode persists across sessions — pick tiles once and it stays until you switch (N14).
  const [view, setViewState] = useState<"fleet" | "table">(
    () => (readView() === "table" ? "table" : "fleet"),
  );
  const setView = (v: "fleet" | "table") => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* private mode / storage disabled — in-memory only, no big deal */
    }
  };
  // Customer-only page switch: their own fleet vs the shared Templates gallery (merged into one page).
  const [section, setSection] = useState<"mine" | "templates">("mine");
  // Switching back to the fleet refetches it — instantiating a template adds a scraper the (already
  // mounted) list wouldn't otherwise see until a manual Refresh (the merge removed the tab remount).
  const goMine = () => {
    setSection("mine");
    reload();
  };
  const [q, setQ] = useState("");
  const [folderFilter, setFolderFilter] = useState("");
  const w = words(operator);
  const clientOf = (id: string) => tenantPage?.clients.find((t) => t.id === id)?.name ?? short(id);

  const scrapers = data ?? [];
  // The open scraper panel lives in the URL: /scrapers/:id opens that scraper (deep-linkable; Back
  // closes it). /scrapers = list; /scrapers/new is the create route (handled by App, not here).
  const openId =
    pathname.startsWith("/scrapers/") && pathname !== "/scrapers/new"
      ? decodeURIComponent(pathname.slice("/scrapers/".length))
      : null;
  const open = openId ? scrapers.find((s) => s.id === openId) ?? null : null;
  // Distinct, sorted folder labels present in the fleet — backs the folder filter (shown only when used).
  const folders = [...new Set(scrapers.map((s) => s.folder).filter((f): f is string => !!f))].sort();
  const history = useFleetHistory(scrapers);

  const importRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState<string | null>(null);
  async function onImportFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked after a failure
    if (!file) return;
    setImporting(true);
    setImportErr(null);
    try {
      let bundle: unknown;
      try {
        bundle = JSON.parse(await file.text());
      } catch {
        throw new Error("that file isn't valid JSON");
      }
      const created = await api<Scraper>("/scrapers/import", {
        method: "POST",
        body: JSON.stringify(bundle),
      });
      // No native popup — reload the fleet and open the imported scraper straight away (in-app feedback).
      reload();
      navigate(`/scrapers/${created.id}`);
    } catch (err) {
      setImportErr("Import failed: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setImporting(false);
    }
  }

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;

  const needle = q.trim().toLowerCase();
  const filtered = scrapers.filter((s) => {
    if (tenantFilter && s.tenant_id !== tenantFilter) return false;
    if (folderFilter && (s.folder ?? "") !== folderFilter) return false;
    return (
      !needle ||
      s.name.toLowerCase().includes(needle) ||
      (s.target_urls?.[0] ?? "").toLowerCase().includes(needle) ||
      (operator && clientOf(s.tenant_id).toLowerCase().includes(needle))
    );
  });

  // Counted over what you can SEE — otherwise drilling into one client would still headline the
  // whole platform's scars.
  const scars = filtered.reduce((n, s) => n + scarCount(history[s.id] ?? []), 0);
  const waiting = filtered.filter((s) => hasCanary(history[s.id] ?? [])).length;
  const broken = filtered.filter((s) => s.last_run_status === "failed").length;
  const clients = new Set(filtered.map((s) => s.tenant_id)).size;

  // Customer view is the merged Scrapers page (their fleet + the Templates gallery under one switch);
  // an operator keeps the cross-tenant Fleet, no switch. The fleet header/actions belong to "mine".
  const showFleet = operator || section === "mine";

  return (
    <div className="stack">
      <div className="section-head">
        <h1>{operator ? "Fleet" : "Scrapers"}</h1>
        {showFleet && (
          <div className="row-gap">
            <input
              placeholder={operator ? "search name / url / client" : "search name / url"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 190 }}
            />
            {folders.length > 0 && (
              <select
                value={folderFilter}
                onChange={(e) => setFolderFilter(e.target.value)}
                title="Filter by folder"
              >
                <option value="">All folders</option>
                {folders.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            )}
            <RefreshButton onClick={reload} />
            {onNew && (
              <>
                <input
                  ref={importRef}
                  type="file"
                  accept="application/json,.json"
                  style={{ display: "none" }}
                  onChange={onImportFile}
                />
                <button
                  className="btn ghost sm"
                  onClick={() => importRef.current?.click()}
                  disabled={importing}
                  title="Import a scraper from an exported .amendie.json file"
                >
                  {importing ? "Importing…" : "Import"}
                </button>
                <button className="btn sm" onClick={onNew}>
                  + New scraper
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Merged page (customer only): own fleet vs the shared Templates gallery. */}
      {!operator && (
        <div className="seg">
          <button aria-selected={section === "mine"} onClick={goMine}>
            My scrapers
          </button>
          <button aria-selected={section === "templates"} onClick={() => setSection("templates")}>
            Templates
          </button>
        </div>
      )}

      {!operator && section === "templates" && <Recipes embedded onNavToFleet={goMine} />}

      {showFleet && (
        <>
      {importErr && (
        <div className="err" role="alert">
          {importErr}
        </div>
      )}

      {/* Drilled in from Clients — say so, and give a way back out. */}
      {operator && tenantFilter && (
        <div className="row-gap">
          <span className="pill hold">client · {clientOf(tenantFilter)}</span>
          <button className="quiet-btn" onClick={() => onTenantFilter?.(null)}>
            show every client
          </button>
        </div>
      )}

      {scrapers.length === 0 && (
        <div className="empty onboard">
          <h3>Create your first scraper</h3>
          <p>
            Paste a URL and let AI generate the extractor for you, or define selectors by hand. It
            runs on a schedule, delivers structured data, and repairs itself when the site changes.
          </p>
          {onNew && (
            <button className="btn" onClick={onNew}>
              + New scraper
            </button>
          )}
        </div>
      )}

      {scrapers.length > 0 && (
        <>
          {/* The ribbons are the fleet — the sentence carries the count, not a stat row. */}
          <p className="fleet-line lead">
            <em>{filtered.length}</em> scraper{filtered.length === 1 ? "" : "s"}
            {operator && !tenantFilter && clients > 1 && (
              <>
                {" "}
                across <em>{clients}</em> clients
              </>
            )}{" "}
            ·{" "}
            {scars === 0 ? (
              `no ${operator ? "scars" : "repairs"}`
            ) : (
              <>
                <em className="gold">{scars}</em> {operator ? "scar" : "repair"}
                {scars === 1 ? "" : "s"}
              </>
            )}{" "}
            ·{" "}
            {waiting === 0 ? (
              "nothing waiting"
            ) : (
              <em className="gold">
                {waiting} fix{waiting === 1 ? "" : "es"} waiting on you
              </em>
            )}
            {broken > 0 && (
              <>
                {" "}
                · <em className="err">{broken} failing now</em>
              </>
            )}
          </p>

          <div className="seg">
            <button
              aria-selected={view === "fleet"}
              onClick={() => setView("fleet")}
              title="Ribbon view"
            >
              <span aria-hidden style={{ marginRight: 6 }}>▬</span>Ribbons
            </button>
            <button
              aria-selected={view === "table"}
              onClick={() => setView("table")}
              title="Table view"
            >
              <span aria-hidden style={{ marginRight: 6 }}>▦</span>Table
            </button>
          </div>

          {filtered.length === 0 && <div className="empty">No scrapers match.</div>}

          {view === "fleet" &&
            filtered.map((s) => {
              const segs = history[s.id] ?? [];
              return (
                <button key={s.id} className="scraper" onClick={() => navigate(`/scrapers/${s.id}`)}>
                  <span className="name">
                    {s.name}
                    {s.folder && (
                      <span className="pill hold sm" style={{ marginLeft: 6 }}>
                        {s.folder}
                      </span>
                    )}
                    {/* Whose is it? Only meaningful when the list spans clients. */}
                    {operator && !tenantFilter && (
                      <small className="client">{clientOf(s.tenant_id)}</small>
                    )}
                    <small title={s.target_urls?.[0] ?? ""}>{s.target_urls?.[0] ?? "—"}</small>
                  </span>
                  <Ribbon segments={segs} />
                  <span className="end">
                    {segs.length > 0 && versionLabel(operator, segs.length)}
                    {hasCanary(segs) && (
                      <>
                        <br />
                        <span className="pill canary">{w.canary}</span>
                      </>
                    )}
                    {!hasCanary(segs) && s.status === "paused" && (
                      <>
                        <br />
                        <span className="pill paused">{w.paused}</span>
                      </>
                    )}
                  </span>
                </button>
              );
            })}

          {view === "fleet" && filtered.length > 0 && <RibbonLegend operator={operator} />}

          {view === "table" && filtered.length > 0 && (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    {operator && <th>Client</th>}
                    <th>Status</th>
                    <th>Last run</th>
                    <th>Mode</th>
                    <th>Target</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id} className={scarCount(history[s.id] ?? []) > 0 ? "is-heal" : undefined}>
                      <td>
                        {s.name}
                        {s.folder && (
                          <span className="pill hold sm" style={{ marginLeft: 6 }}>
                            {s.folder}
                          </span>
                        )}
                      </td>
                      {operator && <td>{clientOf(s.tenant_id)}</td>}
                      <td>
                        <span className={`pill ${s.status}`}>{statusText(operator, s.status)}</span>
                      </td>
                      <td className="nowrap">
                        {s.last_run_status ? (
                          <span className={`pill ${s.last_run_status}`}>
                            {statusText(operator, s.last_run_status)}
                          </span>
                        ) : (
                          <span className="muted">never run</span>
                        )}
                        {s.last_run_at && <span className="muted sm"> · {fmt(s.last_run_at)}</span>}
                      </td>
                      <td>{s.fetch_mode}</td>
                      <td className="truncate" title={s.target_urls?.[0] ?? ""}>
                        {s.target_urls?.[0] ?? "—"}
                      </td>
                      <td className="acts">
                        <button className="btn ghost sm" onClick={() => navigate(`/scrapers/${s.id}`)}>
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
        </>
      )}

      {open && (
        <ScraperPanel
          scraper={open}
          segments={history[open.id] ?? []}
          operator={operator}
          onClose={() => navigate("/scrapers")}
          onChanged={reload}
        />
      )}
    </div>
  );
}

/* =============================================================================
   One scraper, one place. Everything about it used to be five separate modals
   behind seven per-row buttons.
   ========================================================================== */

type PanelTab = "runs" | "data" | "changes" | "share" | "versions" | "settings";

function ScraperPanel({
  scraper,
  segments,
  operator,
  onClose,
  onChanged,
}: {
  scraper: Scraper;
  segments: Segment[];
  operator: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<PanelTab>("runs");
  // Bumped after "Run now" so the Runs tab reloads immediately (no manual Refresh).
  const [runsKey, setRunsKey] = useState(0);
  const [actErr, setActErr] = useState<string | null>(null);
  const confirmDelete = useConfirmAction();
  const scars = scarCount(segments);
  // Lifetime run count + last collect duration for the header (refetched after "Run now").
  const { data: runStats } = useAsync<{ total: number; lastDurationMs: number | null }>(
    () => api(`/scrapers/${scraper.id}/run-stats`),
    [scraper.id, runsKey],
  );

  async function act(path: string, label: string) {
    setActErr(null);
    try {
      await api(path, { method: "POST" });
      onChanged();
    } catch (e) {
      setActErr(`${label} failed: ` + (e instanceof Error ? e.message : String(e)));
    }
  }

  // Run now → trigger, refresh the fleet, then jump to the Runs tab and reload it so the new run shows
  // up right away (previously the user had to hit Refresh).
  async function runNow() {
    setActErr(null);
    try {
      await api(`/scrapers/${scraper.id}/run`, { method: "POST" });
      onChanged();
      setTab("runs");
      setRunsKey((k) => k + 1);
    } catch (e) {
      setActErr("Run failed: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  function remove() {
    confirmDelete.ask({
      title: `Delete "${scraper.name}"?`,
      body: "Its stored runs and results are not removed.",
      confirmLabel: "Delete scraper",
      danger: true,
      run: async () => {
        await api(`/scrapers/${scraper.id}`, { method: "DELETE" });
        onChanged();
        onClose();
      },
    });
  }

  const tabs: { id: PanelTab; label: string }[] = [
    { id: "runs", label: "Runs" },
    { id: "data", label: "Data" },
    { id: "changes", label: "Changes" },
    { id: "share", label: "Share" },
    { id: "versions", label: operator ? "Versions" : "Setups" },
    { id: "settings", label: "Settings" },
  ];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="section-head">
          <div>
            <h2>{scraper.name}</h2>
            <p className="fleet-line" style={{ marginTop: 6 }}>
              <span className="mono">{short(scraper.id)}</span> · {scraper.fetch_mode}
              {runStats && (
                <>
                  {" "}
                  · {runStats.total} run{runStats.total === 1 ? "" : "s"}
                  {runStats.lastDurationMs != null && (
                    <> · last collect {fmtMillis(runStats.lastDurationMs)}</>
                  )}
                </>
              )}
              {scars > 0 && (
                <>
                  {" "}
                  · <span className="gold">repaired {scars}×</span>
                </>
              )}
            </p>
          </div>
          <div className="row-gap">
            <button className="btn ghost sm" onClick={() => void runNow()}>
              Run now
            </button>
            <button
              className="btn ghost sm"
              onClick={() =>
                void act(
                  `/scrapers/${scraper.id}/${scraper.status === "paused" ? "resume" : "pause"}`,
                  scraper.status === "paused" ? "Resume" : "Pause",
                )
              }
            >
              {scraper.status === "paused" ? "Resume" : "Pause"}
            </button>
            <button className="btn ghost sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {actErr && (
          <div className="err" role="alert" style={{ marginTop: "var(--sp3)" }}>
            {actErr}
          </div>
        )}

        {segments.length > 0 && (
          <div style={{ margin: "var(--sp4) 0" }}>
            <Ribbon segments={segments} />
          </div>
        )}

        <RecipeStrip scraper={scraper} operator={operator} onChanged={onChanged} />

        <div className="seg">
          {tabs.map((t) => (
            <button key={t.id} aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "runs" && <RunsBody scraper={scraper} operator={operator} reloadKey={runsKey} />}
        {tab === "data" && <DataBody scraper={scraper} />}
        {tab === "changes" && <ChangesBody scraper={scraper} />}
        {tab === "share" && <ShareBody scraper={scraper} />}
        {tab === "versions" && <VersionsBody scraper={scraper} operator={operator} />}
        {tab === "settings" && (
          <SettingsBody scraper={scraper} operator={operator} onSaved={onChanged} onDelete={remove} />
        )}
      </div>
      {confirmDelete.modal}
    </div>
  );
}

type RecipeConfig = { fields: { name: string; selector: string }[] } | null;
type RecipeStatus = {
  linked: boolean;
  template_id: string | null;
  template_name: string | null;
  template_version: number | null;
  synced_version: number | null;
  has_update: boolean;
  diverged: boolean;
  current_config: RecipeConfig;
  latest_config: RecipeConfig;
  latest_note: string | null;
  instance_count: number | null;
};
type RecipeVersion = { version: number; note: string | null; created_by: string; created_at: string };

/**
 * Recipe sync strip (Phase B) — shown only when a scraper was instantiated from a gallery recipe. Says
 * which recipe + version it's synced to; if the recipe has a newer version it offers a diff preview + a
 * one-click **opt-in** pull (blocked, with a "merge by hand" note, when the instance has diverged —
 * edited or self-healed since it synced). Operators additionally get "push this scraper's fix to the
 * recipe" to promote a good local fix to everyone.
 */
function RecipeStrip({
  scraper,
  operator,
  onChanged,
}: {
  scraper: Scraper;
  operator: boolean;
  onChanged: () => void;
}) {
  const { data, reload } = useAsync<RecipeStatus>(
    () => api(`/scrapers/${scraper.id}/recipe-status`),
    [scraper.id],
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [pushNote, setPushNote] = useState("");
  const [history, setHistory] = useState<RecipeVersion[] | null>(null);
  const confirmUnpublish = useConfirmAction();

  if (!data || !data.linked) return null;

  async function pull() {
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      const r = await api<{ synced_version: number }>(`/scrapers/${scraper.id}/recipe-pull`, {
        method: "POST",
      });
      setNote(`Updated to template v${r.synced_version}.`);
      setShowDiff(false);
      onChanged();
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "pull failed");
    } finally {
      setBusy(false);
    }
  }

  async function push() {
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      const r = await api<{ version: number }>(`/scrapers/${scraper.id}/push-to-template`, {
        method: "POST",
        body: JSON.stringify({ note: pushNote.trim() || undefined }),
      });
      setNote(`Promoted to the template as v${r.version}. Other scrapers can now pull it.`);
      setPushNote("");
      setHistory(null); // force a refetch next time it's opened
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "push failed");
    } finally {
      setBusy(false);
    }
  }

  async function toggleHistory() {
    if (history) {
      setHistory(null);
      return;
    }
    if (!data?.template_id) return;
    try {
      setHistory(await api<RecipeVersion[]>(`/templates/${data.template_id}/versions`));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "history failed");
    }
  }

  function unpublish() {
    const tid = data?.template_id;
    if (!tid) return;
    confirmUnpublish.ask({
      title: `Unpublish template "${data?.template_name ?? ""}"?`,
      body: "It's removed from the gallery. Existing scrapers keep working but can no longer pull updates from it.",
      confirmLabel: "Unpublish",
      danger: true,
      run: async () => {
        await api(`/templates/${tid}`, { method: "DELETE" });
        onChanged();
        reload();
      },
    });
  }

  return (
    <div
      className="mt"
      style={{
        border: "1px solid var(--rule)",
        borderRadius: "var(--r)",
        padding: "var(--sp3)",
        marginTop: "var(--sp3)",
      }}
    >
      <div className="row-gap" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <span>
          From template <b>{data.template_name ?? "—"}</b>
          {data.synced_version != null && <span className="muted sm"> · synced v{data.synced_version}</span>}
          {data.template_version == null && <span className="muted sm"> · template removed</span>}
        </span>
        {data.has_update && !data.diverged && (
          <span className="pill hold">update available · v{data.template_version}</span>
        )}
        {data.has_update && data.diverged && <span className="pill canary">edited — merge by hand</span>}
        {!data.has_update && data.template_version != null && (
          <span className="muted sm">up to date</span>
        )}
      </div>

      {data.has_update && (
        <div className="mt">
          <p className="muted sm" style={{ marginTop: 0 }}>
            {data.diverged
              ? `This scraper was edited or self-healed since it synced, so the template's v${data.template_version} won't be applied automatically. Review the change and merge by hand.`
              : `The template was updated to v${data.template_version}. Review the selector change, then apply it.`}
            {data.latest_note && (
              <>
                {" "}
                Note: <i>{data.latest_note}</i>
              </>
            )}
          </p>
          <div className="row-gap">
            <button className="btn ghost sm" onClick={() => setShowDiff((s) => !s)}>
              {showDiff ? "Hide changes" : "Review changes"}
            </button>
            {!data.diverged && (
              <button className="btn sm" onClick={() => void pull()} disabled={busy}>
                {busy ? "Applying…" : "Apply update"}
              </button>
            )}
          </div>
          {showDiff && <RecipeDiff current={data.current_config} latest={data.latest_config} />}
        </div>
      )}

      <div className="mt">
        <button className="link-btn" onClick={() => void toggleHistory()}>
          {history ? "Hide version history" : "Version history"}
        </button>
        {history && (
          <ul className="mt" style={{ margin: "var(--sp2) 0 0", paddingLeft: "var(--sp4)" }}>
            {history.map((v) => (
              <li key={v.version} className="muted sm">
                <b>v{v.version}</b>
                {v.note ? ` — ${v.note}` : ""} <span className="muted">· {fmt(v.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {operator && (
        <div className="mt" style={{ borderTop: "1px solid var(--rule)", paddingTop: "var(--sp3)", marginTop: "var(--sp3)" }}>
          <div className="row-gap" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
            <span className="muted sm">
              Operator
              {data.instance_count != null && (
                <>
                  {" · used by "}
                  <b>{data.instance_count}</b> scraper{data.instance_count === 1 ? "" : "s"}
                </>
              )}
            </span>
            {data.template_version != null && (
              <button className="btn ghost sm danger" onClick={unpublish} disabled={busy}>
                Unpublish template
              </button>
            )}
          </div>
          <p className="muted sm" style={{ margin: "var(--sp2) 0 0" }}>
            Promote this scraper's current config as the template's next version:
          </p>
          <div className="row-gap mt">
            <input
              placeholder="changelog note (optional — e.g. “healed rating selector”)"
              value={pushNote}
              onChange={(e) => setPushNote(e.target.value)}
              style={{ flex: 1 }}
            />
            <button className="btn ghost sm" onClick={() => void push()} disabled={busy}>
              {busy ? "Pushing…" : "Push to template"}
            </button>
          </div>
        </div>
      )}

      {note && <div className="mt" style={{ color: "var(--ok, #2e7d32)" }}>{note}</div>}
      {err && <div className="err mt">{err}</div>}
      {confirmUnpublish.modal}
    </div>
  );
}

/** Field-level selector diff between the instance's current config and the recipe's latest. */
function RecipeDiff({ current, latest }: { current: RecipeConfig; latest: RecipeConfig }) {
  const cur = new Map((current?.fields ?? []).map((f) => [f.name, f.selector]));
  const latestFields = latest?.fields ?? [];
  const seen = new Set(latestFields.map((f) => f.name));
  const removed = (current?.fields ?? []).filter((f) => !seen.has(f.name));

  return (
    <div className="table-scroll mt">
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>This scraper</th>
            <th>Recipe (new)</th>
          </tr>
        </thead>
        <tbody>
          {latestFields.map((f) => {
            const old = cur.get(f.name);
            const changed = old !== f.selector;
            return (
              <tr key={f.name} className={changed ? "is-heal" : undefined}>
                <td>{f.name}</td>
                <td className="mono sm">{old ?? <span className="muted">— new field</span>}</td>
                <td className="mono sm">{changed ? <b>{f.selector}</b> : f.selector}</td>
              </tr>
            );
          })}
          {removed.map((f) => (
            <tr key={f.name} className="is-heal">
              <td>{f.name}</td>
              <td className="mono sm">{f.selector}</td>
              <td className="muted">— dropped by recipe</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChangesBody({ scraper }: { scraper: Scraper }) {
  const { data, loading, error } = useAsync<ChangesData>(
    () => api(`/scrapers/${scraper.id}/changes`),
    [scraper.id],
  );

  if (loading) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="empty">{error}</div>;
  if (!data) return null;

  const { added, removed, changed } = data.counts;
  return (
    <>
      <div className="stat-row">
        <span>
          <b>{added}</b> added
        </span>
        <span>
          <b>{removed}</b> removed
        </span>
        <span>
          <b>{changed}</b> changed
        </span>
        <span className="mono muted">
          {short(data.from.runId)} → {short(data.to.runId)}
        </span>
      </div>
      {added > 0 && (
        <>
          <h4 className="mt">Added</h4>
          <DataTable rows={data.added} />
        </>
      )}
      {removed > 0 && (
        <>
          <h4 className="mt">Removed</h4>
          <DataTable rows={data.removed} />
        </>
      )}
      {changed > 0 && (
        <>
          <h4 className="mt">Changed</h4>
          <pre className="preview">{JSON.stringify(data.changed, null, 2)}</pre>
        </>
      )}
      {added === 0 && removed === 0 && changed === 0 && (
        <div className="empty">No changes between the last two runs.</div>
      )}
    </>
  );
}

function DataBody({ scraper }: { scraper: Scraper }) {
  const { data, loading, error } = useAsync<LatestData>(
    () => api(`/scrapers/${scraper.id}/latest`),
    [scraper.id],
  );
  const [showRaw, setShowRaw] = useState(false);

  return (
    <>
      {loading && <div className="muted pad">Loading…</div>}
      {error && <div className="empty">{error}</div>}
      {data && (
        <>
          <div className="stat-row">
            <span>
              <b>{data.itemCount ?? "—"}</b> items
            </span>
            <span className="muted">
              run <span className="mono">{short(data.runId)}</span> · {fmt(data.finishedAt)}
            </span>
          </div>
          <div className="mt">
            <DataTable rows={data.data} />
          </div>
          <button className="link-btn mt" onClick={() => setShowRaw((s) => !s)}>
            {showRaw ? "Hide raw JSON" : "View raw JSON"}
          </button>
          {showRaw && <pre className="preview">{JSON.stringify(data.data, null, 2)}</pre>}
        </>
      )}
      <TemplateBox scraper={scraper} />
    </>
  );
}

/**
 * "Use this scraper as a template": paste any same-site URL (e.g. another film) and extract it live
 * with THIS scraper's config via GET /scrapers/:id/extract — no saving, no new URL in the config.
 * Also surfaces the endpoint for programmatic use (feed links from your own code with an API key).
 */
function TemplateBox({ scraper }: { scraper: Scraper }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [res, setRes] = useState<{ valid: boolean; itemCount: number; renderUsed: string; data: unknown } | null>(null);

  async function run() {
    if (!url.trim()) return;
    setBusy(true);
    setErr(null);
    setRes(null);
    try {
      setRes(await api(`/scrapers/${scraper.id}/extract?url=${encodeURIComponent(url.trim())}`));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "extract failed");
    } finally {
      setBusy(false);
    }
  }

  const endpoint = `${window.location.origin}/api/scrapers/${scraper.id}/extract?url=`;
  return (
    <div className="mt" style={{ borderTop: "1px solid var(--rule)", paddingTop: "var(--sp4)", marginTop: "var(--sp5)" }}>
      <h4 style={{ margin: "0 0 4px" }}>Use as template</h4>
      <p className="muted sm" style={{ marginTop: 0 }}>
        Apply this scraper's setup to any other page of the same kind (e.g. a different film) — extract
        it live, nothing is saved. Or call it from your own code as an endpoint.
      </p>
      <div className="row-gap">
        <input
          placeholder="paste a URL — e.g. another film"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void run()}
          style={{ flex: 1 }}
        />
        <button className="btn sm" onClick={() => void run()} disabled={busy || !url.trim()}>
          {busy ? "Extracting…" : "Extract"}
        </button>
      </div>
      {err && <div className="err mt">{err}</div>}
      {res && (
        <div className="mt">
          <div className="stat-row">
            <span className={`pill ${res.valid ? "success" : "failed"}`}>{res.valid ? "looks good" : "needs work"}</span>
            <span className="muted sm">
              <b>{res.itemCount}</b> items · rendered {res.renderUsed}
            </span>
          </div>
          <div className="mt">
            <DataTable rows={res.data} />
          </div>
        </div>
      )}
      <details className="mt">
        <summary className="muted sm" style={{ cursor: "pointer" }}>Endpoint (for your code)</summary>
        <div className="row-gap mt">
          <input readOnly value={`${endpoint}<url>`} onFocus={(e) => e.currentTarget.select()} style={{ flex: 1 }} />
          <CopyButton text={endpoint} label="Copy" />
        </div>
        <p className="muted sm" style={{ marginTop: 6 }}>
          GET, URL-encode the film link. Authenticate with an API key (Settings → API keys):
          <code style={{ display: "block", marginTop: 4, wordBreak: "break-all" }}>
            curl -H "x-api-key: YOUR_KEY" "{endpoint}https%3A%2F%2F…"
          </code>
        </p>
        <p className="muted sm" style={{ marginTop: 6 }}>
          Many links in one call — POST a JSON list (up to 20); each result comes back tagged with its url:
          <code style={{ display: "block", marginTop: 4, wordBreak: "break-all" }}>
            curl -H "x-api-key: YOUR_KEY" -H "content-type: application/json" -d
            {' \'{"urls":["https://…","https://…"]}\' '}
            "{window.location.origin}/api/scrapers/{scraper.id}/extract"
          </code>
        </p>
      </details>
    </div>
  );
}

type ShareLink = { id: string; label: string | null; createdBy: string; createdAt: string };

/** Public read-only share links for a scraper: create (token shown once), list, revoke. */
function ShareBody({ scraper }: { scraper: Scraper }) {
  const { data, loading, error, reload } = useAsync<ShareLink[]>(
    () => api(`/scrapers/${scraper.id}/shares`),
    [scraper.id],
  );
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState<string | null>(null); // freshly-minted absolute URL — shown ONCE
  const [label, setLabel] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const confirmRevoke = useConfirmAction();

  async function create() {
    setCreating(true);
    setErr(null);
    setFresh(null);
    try {
      const res = await api<{ token: string; path: string; link: ShareLink }>(
        `/scrapers/${scraper.id}/share`,
        { method: "POST", body: JSON.stringify({ label: label.trim() || undefined }) },
      );
      setFresh(window.location.origin + res.path);
      setLabel("");
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create failed");
    } finally {
      setCreating(false);
    }
  }

  function revoke(id: string) {
    confirmRevoke.ask({
      title: "Revoke this link?",
      body: "Anyone holding it loses access immediately.",
      confirmLabel: "Revoke link",
      danger: true,
      run: async () => {
        await api(`/scrapers/${scraper.id}/shares/${id}`, { method: "DELETE" });
        reload();
      },
    });
  }

  const links = data ?? [];
  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        A share link is a read-only public page of this scraper's latest results — no login required.
        Anyone with the link can view it until you revoke it.
      </p>
      <div className="row-gap">
        <input
          placeholder="label (optional — e.g. “for Acme”)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          style={{ flex: 1 }}
        />
        <button className="btn sm" onClick={() => void create()} disabled={creating}>
          {creating ? "Creating…" : "+ Create link"}
        </button>
      </div>
      {err && <div className="err mt">{err}</div>}
      {fresh && (
        <div
          className="mt"
          style={{
            border: "1px solid var(--rule)",
            borderRadius: "var(--r)",
            padding: "var(--sp3)",
            background: "var(--wash)",
          }}
        >
          <div className="muted sm">New link — copy it now, it won't be shown again:</div>
          <div className="row-gap" style={{ marginTop: 6 }}>
            <input
              readOnly
              value={fresh}
              onFocus={(e) => e.currentTarget.select()}
              style={{ flex: 1 }}
            />
            <CopyButton text={fresh} label="Copy link" />
          </div>
        </div>
      )}
      {loading && !data && <div className="muted pad">Loading…</div>}
      {error && <div className="err pad">Error: {error}</div>}
      {!loading && links.length === 0 && <div className="empty mt">No active links.</div>}
      {links.length > 0 && (
        <div className="table-scroll mt">
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.id}>
                  <td>{l.label || <span className="muted">unlabeled</span>}</td>
                  <td className="nowrap">
                    {fmt(l.createdAt)} <span className="muted sm">· {l.createdBy}</span>
                  </td>
                  <td className="acts">
                    <button className="btn ghost sm" onClick={() => revoke(l.id)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirmRevoke.modal}
    </>
  );
}

function SettingsBody({
  scraper,
  operator,
  onSaved,
  onDelete,
}: {
  scraper: Scraper;
  operator: boolean;
  onSaved: () => void;
  onDelete: () => void;
}) {
  const { data } = useAsync<{
    name: string;
    schedule: string | null;
    webhook_url: string | null;
    folder: string | null;
    rate_limit_rps: number | null;
  }>(() => api(`/scrapers/${scraper.id}`), [scraper.id]);
  const [name, setName] = useState(scraper.name);
  const [schedule, setSchedule] = useState(scraper.schedule ?? "");
  const [webhook, setWebhook] = useState("");
  const [folder, setFolder] = useState("");
  const [rps, setRps] = useState("");
  const [customCron, setCustomCron] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (data && !loaded.current) {
      loaded.current = true;
      setName(data.name);
      setSchedule(data.schedule ?? "");
      setWebhook(data.webhook_url ?? "");
      setFolder(data.folder ?? "");
      setRps(data.rate_limit_rps != null ? String(data.rate_limit_rps) : "");
      setCustomCron(!!data.schedule && !SCHEDULES.some((s) => s.cron === data.schedule));
    }
  }, [data]);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const rpsNum = rps.trim() === "" ? null : Number(rps);
      if (rpsNum !== null && (!Number.isFinite(rpsNum) || rpsNum <= 0 || rpsNum > 20)) {
        setErr("Rate limit must be a number between 0 and 20 requests/second");
        setBusy(false);
        return;
      }
      await api(`/scrapers/${scraper.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: name.trim(),
          schedule: schedule.trim(),
          webhook_url: webhook.trim() || null,
          folder: folder.trim() || null,
          rate_limit_rps: rpsNum,
        }),
      });
      setSavedAt(new Date().toLocaleTimeString());
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="form-grid">
        <label htmlFor="sc-name">Name</label>
        <input id="sc-name" value={name} onChange={(e) => setName(e.target.value)} />

        <label htmlFor="sc-sched">Schedule</label>
        <div>
          <select
            id="sc-sched"
            value={customCron ? "__custom__" : schedule}
            onChange={(e) => {
              if (e.target.value === "__custom__") setCustomCron(true);
              else {
                setCustomCron(false);
                setSchedule(e.target.value);
              }
            }}
          >
            {SCHEDULES.map((s) => (
              <option key={s.label} value={s.cron}>
                {s.label}
              </option>
            ))}
          </select>
          {customCron && (
            <input
              className="mt"
              placeholder="cron — e.g. 0 */6 * * *"
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
            />
          )}
        </div>

        <label htmlFor="sc-hook">Webhook</label>
        <input
          id="sc-hook"
          placeholder="optional — POST results to this URL"
          value={webhook}
          onChange={(e) => setWebhook(e.target.value)}
        />

        <label htmlFor="sc-folder">Folder</label>
        <input
          id="sc-folder"
          placeholder="optional — group this scraper (e.g. Competitors)"
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
        />

        <label htmlFor="sc-rps">Rate limit</label>
        <input
          id="sc-rps"
          type="number"
          min="0.1"
          max="20"
          step="0.1"
          placeholder="requests/sec — blank = default politeness"
          value={rps}
          onChange={(e) => setRps(e.target.value)}
        />
      </div>

      <RequestAuthPanel scraperId={scraper.id} />

      {err && <div className="err mt">{err}</div>}
      <div className="row-gap mt" style={{ marginTop: "var(--sp5)" }}>
        <button className="btn danger sm" onClick={onDelete}>
          Delete scraper
        </button>
        <button
          className="btn ghost sm"
          title="Download this scraper as a portable .amendie.json (move it to another workspace)"
          onClick={() =>
            void download(`/scrapers/${scraper.id}/export`, `${scraper.name}.amendie.json`).catch((e) =>
              setErr("Export failed: " + (e instanceof Error ? e.message : String(e))),
            )
          }
        >
          Export JSON
        </button>
        <div style={{ flex: 1 }} />
        {savedAt && <span className="muted sm">Saved {savedAt}</span>}
        <button className="btn" onClick={() => void save()} disabled={busy}>
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
      <PublishTemplateBox scraper={scraper} operator={operator} />
    </>
  );
}

/**
 * Edit the per-scraper request auth (headers + cookie) on an EXISTING scraper — e.g. refresh an expired
 * cf_clearance / session cookie without re-creating it. WRITE-ONLY: the GET returns only metadata (which
 * header names, whether a cookie is set) — secret values are never echoed back. Saving mints a new
 * extractor version (history + rollback), like any config change.
 */
function RequestAuthPanel({ scraperId }: { scraperId: string }) {
  const [status, setStatus] = useState<{ hasAuth: boolean; headerNames: string[]; hasCookies: boolean } | null>(null);
  const [open, setOpen] = useState(false);
  const [cookies, setCookies] = useState("");
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    api<{ hasAuth: boolean; headerNames: string[]; hasCookies: boolean }>(`/scrapers/${scraperId}/request-auth`)
      .then(setStatus)
      .catch(() => setStatus(null));
  useEffect(() => {
    void load();
  }, [scraperId]);

  async function save() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const h: Record<string, string> = {};
      for (const r of headers) {
        const k = r.key.trim();
        if (k) h[k] = r.value;
      }
      const request: Record<string, unknown> = {};
      if (Object.keys(h).length) request.headers = h;
      if (cookies.trim()) request.cookies = cookies.trim();
      await api(`/scrapers/${scraperId}/request-auth`, {
        method: "PUT",
        body: JSON.stringify({ request: Object.keys(request).length ? request : null }),
      });
      setCookies("");
      setHeaders([]);
      setMsg("Saved — a new extractor version was created.");
      void load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "save failed");
    } finally {
      setBusy(false);
    }
  }

  async function clearAuth() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await api(`/scrapers/${scraperId}/request-auth`, { method: "PUT", body: JSON.stringify({ request: null }) });
      setMsg("Auth cleared.");
      void load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "clear failed");
    } finally {
      setBusy(false);
    }
  }

  const summary = status
    ? status.hasAuth
      ? `set — ${[...status.headerNames, status.hasCookies ? "cookie" : ""].filter(Boolean).join(", ")}`
      : "none"
    : "…";

  return (
    <div className="mt" style={{ borderTop: "1px solid var(--rule)", paddingTop: "var(--sp4)" }}>
      <button className="btn ghost sm" type="button" onClick={() => setOpen((o) => !o)}>
        {open ? "▾ Authentication" : "▸ Authentication"}
      </button>{" "}
      <span className="muted sm">· {summary}</span>
      {open && (
        <div className="advanced mt">
          <p className="muted sm" style={{ marginTop: 0 }}>
            Set or refresh request auth (headers + cookie — e.g. an expired{" "}
            <span className="mono">cf_clearance</span>). Values are <b>write-only</b> — enter NEW values to
            replace; saving creates a new extractor version. Sent same-origin only; never exported.
          </p>
          <div className="form-grid">
            <label>Cookies</label>
            <textarea
              rows={2}
              placeholder="raw Cookie — e.g. cf_clearance=…; sessionid=…"
              value={cookies}
              onChange={(e) => setCookies(e.target.value)}
            />
            <label>Headers</label>
            <div className="actions-editor">
              {headers.map((h, i) => (
                <div key={i} className="action-row">
                  <input
                    placeholder="header — e.g. Authorization"
                    value={h.key}
                    onChange={(e) => setHeaders((xs) => xs.map((x, idx) => (idx === i ? { ...x, key: e.target.value } : x)))}
                    style={{ flex: 1, minWidth: 120 }}
                  />
                  <input
                    placeholder="value"
                    value={h.value}
                    onChange={(e) => setHeaders((xs) => xs.map((x, idx) => (idx === i ? { ...x, value: e.target.value } : x)))}
                    style={{ flex: 1, minWidth: 120 }}
                  />
                  <button type="button" className="btn ghost sm" onClick={() => setHeaders((xs) => xs.filter((_, idx) => idx !== i))}>
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="btn ghost sm" onClick={() => setHeaders((xs) => [...xs, { key: "", value: "" }])}>
                + Add header
              </button>
            </div>
          </div>
          {err && <div className="err mt">{err}</div>}
          {msg && <div className="note mt">{msg}</div>}
          <div className="row-gap mt">
            <button className="btn sm" disabled={busy} onClick={() => void save()}>
              {busy ? "Saving…" : "Save auth"}
            </button>
            {status?.hasAuth && (
              <button className="btn ghost sm" disabled={busy} onClick={() => void clearAuth()}>
                Clear auth
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Publish this scraper's config as a template (POST /scrapers/:id/publish-template). An OPERATOR publishes
 * into the SHARED gallery (any tenant can instantiate); a CUSTOMER publishes a PERSONAL template scoped to
 * their own workspace (only they see + reuse it). The API decides the scope from the caller's role; here
 * we only adapt the copy. In-app feedback only — no native popups.
 */
function PublishTemplateBox({ scraper, operator }: { scraper: Scraper; operator: boolean }) {
  const [openForm, setOpenForm] = useState(false);
  const [name, setName] = useState(scraper.name);
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [doneSlug, setDoneSlug] = useState<string | null>(null);

  async function publish() {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const t = await api<{ slug: string }>(`/scrapers/${scraper.id}/publish-template`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), description: description.trim() || undefined }),
      });
      setDoneSlug(t.slug);
      setOpenForm(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "publish failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="mt"
      style={{ borderTop: "1px solid var(--rule)", paddingTop: "var(--sp4)", marginTop: "var(--sp5)" }}
    >
      <h4 style={{ margin: "0 0 4px" }}>
        {operator ? "Publish as template" : "Save as personal template"}
      </h4>
      <p className="muted sm" style={{ marginTop: 0 }}>
        {operator ? (
          <>
            Add this scraper's setup to the shared <b>Templates</b> gallery. Any workspace can then create a
            scraper from it in one click — no need to rebuild the same extractor for the same site.
          </>
        ) : (
          <>
            Save this scraper's setup as a <b>personal template</b> in your workspace — reuse it to spin up
            new scrapers of the same kind. It stays private to you (it appears under <b>Templates</b>).
          </>
        )}
      </p>
      {doneSlug && (
        <div className="mt" style={{ color: "var(--ok, #2e7d32)" }}>
          {operator ? "Published to the gallery" : "Saved as a personal template"} as{" "}
          <span className="mono">{doneSlug}</span>.
        </div>
      )}
      {!openForm && (
        <button className="btn ghost sm" onClick={() => { setOpenForm(true); setDoneSlug(null); }}>
          {doneSlug
            ? operator
              ? "Publish again"
              : "Save again"
            : operator
              ? "Publish as template"
              : "Save as personal template"}
        </button>
      )}
      {openForm && (
        <div className="form-grid mt">
          <label htmlFor="tpl-name">Template name</label>
          <input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} />
          <label htmlFor="tpl-desc">Description</label>
          <input
            id="tpl-desc"
            placeholder="optional — what this template scrapes"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div />
          <div className="row-gap">
            <button className="btn sm" onClick={() => void publish()} disabled={busy || !name.trim()}>
              {busy ? "Publishing…" : "Publish"}
            </button>
            <button className="btn ghost sm" onClick={() => setOpenForm(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {err && <div className="err mt">{err}</div>}
    </div>
  );
}

function VersionsBody({ scraper, operator }: { scraper: Scraper; operator: boolean }) {
  const { data, loading, error, reload } = useAsync<ExtractorVersion[]>(
    () => api(`/scrapers/${scraper.id}/versions`),
    [scraper.id],
  );
  const versions = [...(data ?? [])].sort(
    (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
  );
  const confirmRollback = useConfirmAction();

  function rollback(vid: string) {
    confirmRollback.ask({
      title: "Roll back to this version?",
      body: "It becomes the active extractor.",
      confirmLabel: "Roll back",
      run: async () => {
        await api(`/scrapers/${scraper.id}/versions/${vid}/rollback`, { method: "POST" });
        reload();
      },
    });
  }

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  if (versions.length === 0) return <div className="empty">Nothing here yet.</div>;

  // extractor_versions.parent_version_id is a real tree — gold edge where the
  // child was born from a heal, plain edge where a human made it.
  return (
    <>
      <ul className="lineage">
      {versions.map((v, i) => {
        const heal = v.created_by === "ai";
        const cls = [
          heal ? "from-heal" : "",
          v.status === "active" ? "is-active" : "",
          v.status === "canary" ? "is-canary" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <li key={v.id} className={cls}>
            <span className="edge">
              <span className="node" />
            </span>
            <span>
              <span style={{ fontSize: "var(--t-cap)" }}>
                {versionLabel(operator, i + 1)} {heal ? "— repaired by Amendie" : "— made by hand"}
              </span>
              <small className="muted" style={{ display: "block", fontSize: "var(--t-micro)" }}>
                <span className="mono">{short(v.id)}</span> · {fmt(v.created_at)}
              </small>
            </span>
            <span className="row-gap">
              <span className={`pill ${v.status}`}>{statusText(operator, v.status)}</span>
              {v.status !== "active" && (
                <button className="btn ghost sm" onClick={() => rollback(v.id)}>
                  Roll back
                </button>
              )}
            </span>
          </li>
        );
      })}
      </ul>
      {confirmRollback.modal}
    </>
  );
}

/** Human-friendly framing for the failure classes (raw codes mean nothing to a customer). */
const FAILURE: Record<string, { label: string; hint: string }> = {
  anti_bot: {
    label: "Blocked (anti-bot)",
    hint: "The site blocked the request. Try enabling JS render on the scraper; Amendie may also retry.",
  },
  structural: {
    label: "Site changed",
    hint: "The page layout changed — Amendie auto-repairs the extractor when this happens, off the hot path.",
  },
  transient: {
    label: "Temporary error",
    hint: "A transient network/site hiccup; it usually clears on the next run.",
  },
};

function RunsBody({
  scraper,
  operator,
  reloadKey = 0,
}: {
  scraper: Scraper;
  operator: boolean;
  /** Bumped by the panel after "Run now" to force an immediate reload of the run list. */
  reloadKey?: number;
}) {
  // Runs refresh themselves on a 10s poll while this tab is open — no manual toggle, no button (N2).
  const { data, loading, error } = useAsync<Run[]>(
    () => api(`/scrapers/${scraper.id}/runs`),
    [scraper.id, reloadKey],
    10_000,
  );
  const runs = data ?? [];
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [dlErr, setDlErr] = useState<string | null>(null);

  async function csv(runId: string) {
    setDlErr(null);
    try {
      await download(`/runs/${runId}/result?format=csv`, `${scraper.name}-${short(runId)}.csv`);
    } catch (e) {
      setDlErr("Download failed: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  async function viewSnapshot(runId: string) {
    setDlErr(null);
    try {
      setSnapshot(await apiText(`/runs/${runId}/snapshot`));
    } catch (e) {
      setDlErr("No snapshot: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  return (
    <>
      {dlErr && (
        <div className="err" role="alert" style={{ marginBottom: "var(--sp3)" }}>
          {dlErr}
        </div>
      )}
      {loading && !data && <div className="muted pad">Loading…</div>}
      {error && <div className="err pad">Error: {error}</div>}
      {!loading && runs.length === 0 && <div className="empty">No runs yet.</div>}
      {runs.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Items</th>
                <th>Null%</th>
                <th>Failure</th>
                <th>Started</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>
                    <span className={`pill ${r.status}`}>{statusText(operator, r.status)}</span>
                  </td>
                  <td>{r.item_count ?? "—"}</td>
                  <td>{r.null_rate != null ? `${Math.round(r.null_rate * 100)}%` : "—"}</td>
                  <td>
                    {r.failure_class ? (
                      <span className="failure-cell" title={FAILURE[r.failure_class]?.hint ?? ""}>
                        {FAILURE[r.failure_class]?.label ?? r.failure_class}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="nowrap">{fmt(r.started_at)}</td>
                  <td className="acts">
                    {r.status === "success" && (
                      <button className="btn ghost sm" onClick={() => csv(r.id)}>
                        CSV
                      </button>
                    )}
                    {r.status === "failed" && (
                      <button className="btn ghost sm" onClick={() => viewSnapshot(r.id)}>
                        Snapshot
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {snapshot !== null && <SnapshotModal html={snapshot} onClose={() => setSnapshot(null)} />}
    </>
  );
}

function SnapshotModal({ html, onClose }: { html: string; onClose: () => void }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "90vw", height: "85vh" }}
      >
        <div className="section-head">
          <h2>HTML snapshot</h2>
          <button className="btn ghost sm" onClick={onClose}>
            Close
          </button>
        </div>
        {/* sandbox="" = maximally restrictive (no scripts, no same-origin, no forms). The stored
            HTML is attacker-controlled, so it renders inert here — stored-XSS safe. */}
        <iframe
          title="snapshot"
          sandbox=""
          srcDoc={html}
          style={{
            width: "100%",
            height: "88%",
            border: "1px solid var(--rule)",
            borderRadius: "var(--r)",
            background: "#fff",
            marginTop: "var(--sp3)",
          }}
        />
      </div>
    </div>
  );
}
